import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  useMediaQuery,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { GoogleLogin } from '@react-oauth/google';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { GoogleSignIn } from 'capacitor-google-sign-in';
import axios from 'axios';
import { persistentStorage } from '../utils/storage';
import { makeStyles } from '@mui/styles';
import ParkingImage from '../assets/nimbleparknewlogo.svg';
import { motion, AnimatePresence } from 'framer-motion';
import config from '../config';
import { markSessionStart } from '../utils/session';
import FacebookIcon from '@mui/icons-material/Facebook';
import GoogleIcon from '@mui/icons-material/Google';
import AppleIcon from '@mui/icons-material/Apple';
import TermsOfService from './TermsOfService';
import PrivacyPolicy from './PrivacyPolicy';

const NativeAppleSignIn = registerPlugin('NativeAppleSignIn');

// Login responses occasionally come back with the id under a different field
// name (e.g. `id` vs `user_id`). Storing `undefined` silently deletes the key,
// which then breaks downstream — push registration, /bookings calls, etc.
// Resolve once, warn loudly if missing, return whatever we found.
function resolveUserId(user, source) {
  const id = user && (user.user_id ?? user.userId ?? user.id);
  if (id === undefined || id === null) {
    const keys = user ? Object.keys(user).join(', ') : '(no user object)';
    console.error(`[Login:${source}] response missing user_id — got keys: ${keys}`);
  }
  return id;
}


const useStyles = makeStyles(() => ({
  rootContainer: {
    minHeight: '100vh',
    minHeight: '100dvh', // Dynamic viewport height for mobile
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: 'white',
    overflow: 'hidden',
    fontFamily: 'Nunito, Poppins, Sansation, sans-serif',
  },
  loginCard: {
    width: '100%',
    height: '100%',
    minHeight: '100vh',
    minHeight: '100dvh', // Dynamic viewport height for mobile
    position: 'relative',
    background: 'white',
    overflow: 'hidden',
    borderRadius: 0, // Remove border radius for full screen
    display: 'flex',
    flexDirection: 'column',
  },
  logo: {
    width: 126,
    height: 114,
  },
  tagline: {
    color: '#C084FC',
    fontSize: 18,
    fontFamily: 'Sansation',
    fontWeight: '700',
    lineHeight: '19.10px',
    textAlign: 'center',
  },
  phoneInput: {
    width: '100%',
    height: 51,
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      border: '1.04px #CCCCCC solid',
      '& fieldset': {
        border: '1.04px #CCCCCC solid',
      },
      '&:hover fieldset': {
        border: '1.04px #CCCCCC solid',
      },
      '&.Mui-focused fieldset': {
        border: '1.04px #CCCCCC solid',
      },
    },
    '& .MuiInputBase-input': {
      padding: '15px 16px',
      fontSize: 16,
      fontFamily: 'Nunito',
    },
  },
  otpInput: {
    width: '100%',
    height: 51,
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      border: '1.04px #CCCCCC solid',
      '& fieldset': {
        border: '1.04px #CCCCCC solid',
      },
      '&:hover fieldset': {
        border: '1.04px #CCCCCC solid',
      },
      '&.Mui-focused fieldset': {
        border: '1.04px #CCCCCC solid',
      },
    },
    '& .MuiInputBase-input': {
      padding: '15px 16px',
      fontSize: 16,
      fontFamily: 'Nunito',
    },
  },
  userWelcome: {
    position: 'relative',
    marginTop: 20,
    marginBottom: 20,
    textAlign: 'center',
    color: '#C084FC',
    fontSize: 16,
    fontFamily: 'Nunito',
    fontWeight: '600',
    maxWidth: '80%',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  signInButton: {
    width: '100%',
    height: 51,
    background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
    boxShadow: '0px 9px 14px rgba(192, 132, 252, 0.20)',
    borderRadius: 10.40,
    border: 'none',
    color: 'white',
    fontSize: 18,
    fontFamily: 'Nunito',
    fontWeight: '700',
    lineHeight: '33.19px',
    cursor: 'pointer',
    '&:hover': {
      background: 'linear-gradient(90deg, #B574E6 0%, #6A4A8A 100%)',
    },
    '&:disabled': {
      background: '#E0E0E0',
      color: '#9E9E9E',
    },
  },
  signUpText: {
    textAlign: 'center',
    fontSize: 14,
    fontFamily: 'Poppins',
    fontWeight: '500',
    wordWrap: 'break-word',
  },
  orText: {
    left: 173,
    top: 537,
    position: 'absolute',
    textAlign: 'center',
    color: 'black',
    fontSize: 14,
    fontFamily: 'Poppins',
    fontWeight: '500',
    wordWrap: 'break-word',
  },
  orLineLeft: {
    width: 103,
    height: 0,
    left: 38,
    top: 549,
    position: 'absolute',
    outline: '1px black solid',
    outlineOffset: '-0.50px',
  },
  orLineRight: {
    width: 103,
    height: 0,
    left: 218,
    top: 549,
    position: 'absolute',
    outline: '1px black solid',
    outlineOffset: '-0.50px',
  },
  continueText: {
    left: 74,
    top: 575,
    position: 'absolute',
    textAlign: 'center',
    color: '#B6B6B6',
    fontSize: 14,
    fontFamily: 'Poppins',
    fontWeight: '500',
    wordWrap: 'break-word',
  },
  socialButtonsContainer: {
    position: 'absolute',
    top: 618,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: 20,
    alignItems: 'center',
  },
  facebookButton: {
    width: 58,
    height: 54,
    background: '#0866FF',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      background: '#0756D6',
    },
  },
  googleButton: {
    width: 81,
    height: 76,
    background: 'white',
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      background: '#F5F5F5',
    },
  },
  appleButton: {
    width: 62,
    height: 57,
    background: 'black',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      background: '#333',
    },
  },
  errorText: {
    color: '#f44336',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
    fontFamily: 'Nunito',
  },
  loadingSpinner: {
    color: 'white',
  },
}));

const Login = ({ setRole }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [loginMethod, setLoginMethod] = useState('check'); // 'check', 'otp', 'password'
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [fbReady, setFbReady] = useState(false);
  const [appleReady, setAppleReady] = useState(false);

  // Load Facebook SDK on web only. Native (Android/iOS) FB login is phase 2.
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    if (!config.FACEBOOK_APP_ID) return;
    if (typeof window === 'undefined') return;
    if (window.FB) {
      setFbReady(true);
      return;
    }
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: config.FACEBOOK_APP_ID,
        cookie: false,
        xfbml: false,
        version: 'v19.0',
      });
      setFbReady(true);
    };
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      document.body.appendChild(script);
    }
  }, []);

  // Load Sign in with Apple JS on web only. Native iOS uses AuthenticationServices.
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    if (!config.APPLE_CLIENT_ID) return;
    if (typeof window === 'undefined') return;

    const initAppleID = () => {
      if (!window.AppleID || !window.AppleID.auth) return;
      try {
        window.AppleID.auth.init({
          clientId: config.APPLE_CLIENT_ID,
          scope: 'name email',
          redirectURI: config.APPLE_REDIRECT_URI || window.location.origin,
          usePopup: true,
        });
        setAppleReady(true);
      } catch (e) {
        console.error('AppleID.auth.init failed:', e);
      }
    };

    if (window.AppleID && window.AppleID.auth) {
      initAppleID();
      return;
    }
    const existing = document.getElementById('appleid-signin-sdk');
    if (existing) {
      existing.addEventListener('load', initAppleID, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = 'appleid-signin-sdk';
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.defer = true;
    script.onload = initAppleID;
    script.onerror = () => console.error('Failed to load Apple Sign In SDK');
    document.body.appendChild(script);
  }, []);

  // Shared post-login persistence + navigation for SSO flows (Google + Facebook).
  const completeCustomerLogin = async (user) => {
    await persistentStorage.set('currentUser', JSON.stringify(user));
    await persistentStorage.set('currentUserId', resolveUserId(user, 'login'));
    await persistentStorage.set('currentUserEmail', user.email);
    if (user.name) {
      await persistentStorage.set('currentUserName', user.name);
    }
    await persistentStorage.remove('currentUserPhone');
    if (setRole && user.role) {
      setRole(user.role);
    }
    await markSessionStart();
    navigate('/user-dashboard');
  };

  const handleWebFacebookSSO = async () => {
    setError('');
    if (!config.FACEBOOK_APP_ID) {
      setError('Facebook login is not configured.');
      return;
    }
    if (!window.FB || !fbReady) {
      setError('Facebook SDK is still loading. Please try again in a moment.');
      return;
    }
    setIsLoading(true);
    // FB.login rejects async callbacks ("Expression is of type asyncfunction, not function"),
    // so the callback must be a plain function that kicks off the async work itself.
    window.FB.login(
      (response) => {
        (async () => {
          try {
            if (response.status !== 'connected' || !response.authResponse?.accessToken) {
              if (response.status !== 'connected') {
                setError(response.status === 'not_authorized'
                  ? 'Facebook login was cancelled or not authorized.'
                  : 'Facebook login failed. Please try again.');
              }
              setIsLoading(false);
              return;
            }
            const accessToken = response.authResponse.accessToken;
            const apiResp = await axios.post(`${config.API_BASE_URL}/facebook-login`, {
              accessToken,
            }, { headers: { 'Content-Type': 'application/json' } });

            if (apiResp.data && apiResp.data.user) {
              await completeCustomerLogin(apiResp.data.user);
            } else {
              setError('Login failed. No user data received.');
            }
          } catch (err) {
            console.error('Facebook SSO Error:', err);
            const msg = err.response?.data?.error || err.message || 'Facebook login failed. Please try again.';
            setError(msg);
          } finally {
            setIsLoading(false);
          }
        })();
      },
      { scope: 'email,public_profile' }
    );
  };

  const handleWebAppleSSO = async () => {
    setError('');
    if (!config.APPLE_CLIENT_ID) {
      setError('Apple Sign In is not configured.');
      return;
    }
    if (!window.AppleID || !window.AppleID.auth || !appleReady) {
      setError('Apple Sign In is still loading. Please try again in a moment.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await window.AppleID.auth.signIn();
      const idToken = response?.authorization?.id_token;
      if (!idToken) {
        setError('Apple Sign In did not return an identity token.');
        return;
      }
      // Apple only includes `user` on the FIRST sign-in. Forward it so the
      // backend can persist first/last name on initial account creation.
      const apiResp = await axios.post(
        `${config.API_BASE_URL}/apple-login`,
        { id_token: idToken, user: response.user || null },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (apiResp.data && apiResp.data.user) {
        await completeCustomerLogin(apiResp.data.user);
      } else {
        setError('Login failed. No user data received.');
      }
    } catch (err) {
      // AppleID.auth.signIn() rejects with {error: 'popup_closed_by_user' | 'user_cancelled_authorize' | ...}
      const appleErr = err && err.error;
      if (appleErr === 'popup_closed_by_user' || appleErr === 'user_cancelled_authorize') {
        // Silent: user dismissed the dialog.
      } else {
        console.error('Apple SSO Error:', err);
        const msg = err?.response?.data?.error || err?.message || appleErr || 'Apple Sign In failed. Please try again.';
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleNativeAppleSSO = async () => {
    setError('');
    if (Capacitor.getPlatform() !== 'ios') {
      setError('Apple Sign In is only available on iOS.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await NativeAppleSignIn.signIn();
      const idToken = response?.identityToken;
      if (!idToken) {
        setError('Apple Sign In did not return an identity token.');
        return;
      }
      const apiResp = await axios.post(
        `${config.API_BASE_URL}/apple-login`,
        { id_token: idToken, user: response?.user || null },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (apiResp.data && apiResp.data.user) {
        await completeCustomerLogin(apiResp.data.user);
      } else {
        setError('Login failed. No user data received.');
      }
    } catch (err) {
      if (err?.code !== 'USER_CANCELLED') {
        console.error('Native Apple SSO Error:', err);
        const msg = err?.response?.data?.error || err?.message || 'Apple Sign In failed. Please try again.';
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckUser = async () => {
    if (!/^[1-9]\d{9}$/.test(phone)) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/check-user-login-method`,
        { phone }
      );
      
      const { exists, login_method, user } = response.data;
      
      if (exists) {
        setUserInfo(user);
        if (login_method === 'password') {
          // Show password login
          setLoginMethod('password');
        } else {
          // OTP login
        setLoginMethod('otp');
        await handleSendOtp();
        }
      } else {
        // New user: OTP flow
        setLoginMethod('otp');
        await handleSendOtp();
      }
    } catch (err) {
      console.error('Check user error:', err);
      setError(err.response?.data?.error || 'User not registered.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/generate-otp`,
        { phone }
      );
      
      if (response.status === 200 || response.status === 201) {
        setLoginMethod('otp');
      } else {
        setError('Failed to send OTP. Please try again.');
      }
    } catch (err) {
      console.error('OTP error:', err);
      const msg = err.response?.data?.error || 'Could not send OTP. Try again.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };


  const handlePhoneLogin = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Step 1: Phone Login
      const response = await axios.post(`${config.API_BASE_URL}/phone-login`, {
        phone,
        otp,
      });

      const user = response.data.user;
      
      // Step 2: Record Login Time
      await axios.post(`${config.API_BASE_URL}/user-status/login`, {
        user_id: user.user_id
      });
      console.log("✅ Login time recorded");

      // Step 3: Get hotel_id from bookings if user.hotel_id is null
      let hotelId = user.hotel_id;
      if (!hotelId) {
        try {
          const hotelResponse = await axios.get(`${config.API_BASE_URL}/get-latest-hotel`, {
            params: { phone: user.phone }
          });
          hotelId = hotelResponse.data.hotel_id;
        } catch (hotelError) {
          console.error('Error fetching hotel from bookings:', hotelError);
        }
      }

      // Step 4: Store User Data in persistent storage (survives app restarts on mobile)
      await persistentStorage.set('currentUserName', user.name);
      await persistentStorage.set('currentRole', 'customer');
      await persistentStorage.set('hotel_id', hotelId);
      await persistentStorage.set('currentUserPhone', user.phone);
      await persistentStorage.set('currentUserId', resolveUserId(user, 'login'));

      // Mark session start (TTL)
      await markSessionStart();
      // Redirect based on role
      navigate('/user-dashboard');

    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordLogin = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/customer-password-login`,
        { phone, password }
      );

      const user = response.data.user;
      
      // Record Login Time
      await axios.post(`${config.API_BASE_URL}/user-status/login`, {
        user_id: user.user_id
      });
      console.log("✅ Login time recorded");

      // Get hotel_id from bookings if user.hotel_id is null
      let hotelId = user.hotel_id;
      if (!hotelId) {
        try {
          const hotelResponse = await axios.get(`${config.API_BASE_URL}/get-latest-hotel`, {
            params: { phone: user.phone }
          });
          hotelId = hotelResponse.data.hotel_id;
        } catch (hotelError) {
          console.error('Error fetching hotel from bookings:', hotelError);
        }
      }

      // Store User Data in persistent storage (survives app restarts on mobile)
      await persistentStorage.set('currentUserName', user.name);
      await persistentStorage.set('currentRole', 'customer');
      await persistentStorage.set('hotel_id', hotelId);
      await persistentStorage.set('currentUserPhone', user.phone);
      await persistentStorage.set('currentUserId', resolveUserId(user, 'login'));

      // Mark session start (TTL)
      await markSessionStart();
      // Redirect to user dashboard
      navigate('/user-dashboard');

    } catch (err) {
      console.error('Password login error:', err);
      setError(err.response?.data?.error || 'Password login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Google Sign-In for native platforms (Android/iOS)
  const handleNativeGoogleSignIn = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      console.log('Native Google Sign-In: Initiating...');
      console.log('Platform:', Capacitor.getPlatform());
      console.log('GoogleSignIn plugin object:', GoogleSignIn);
      console.log('Available methods:', GoogleSignIn ? Object.keys(GoogleSignIn) : 'Plugin not available');
      
      // Check if GoogleSignIn plugin is available
      if (!GoogleSignIn) {
        throw new Error('Google Sign-In plugin is not available. Please ensure capacitor-google-sign-in is properly installed and synced.');
      }
      
      // Check if handleSignInButton method exists (this plugin uses handleSignInButton, not signIn)
      if (!GoogleSignIn.handleSignInButton) {
        throw new Error('GoogleSignIn.handleSignInButton method is not available. The plugin may not be properly synced. Run: npx cap sync android');
      }
      
      // Sign in with Google using handleSignInButton
      // This plugin handles the sign-in button natively
      console.log('Calling GoogleSignIn.handleSignInButton()...');
      const result = await GoogleSignIn.handleSignInButton();
      console.log('Native Google Sign-In result:', JSON.stringify(result, null, 2));
      console.log('Result type:', typeof result);
      console.log('Result keys:', result ? Object.keys(result) : 'null');
      
      // Extract token to send to backend - check multiple possible locations
      let identityToken = null;
      if (result?.response?.identityToken) {
        identityToken = result.response.identityToken;
        console.log('Found identityToken in result.response.identityToken');
      } else if (result?.identityToken) {
        identityToken = result.identityToken;
        console.log('Found identityToken in result.identityToken');
      } else if (result?.authentication?.idToken) {
        identityToken = result.authentication.idToken;
        console.log('Found idToken in result.authentication.idToken');
      } else if (result?.response?.authorizationCode) {
        // Some implementations put the JWT-like token in authorizationCode
        identityToken = result.response.authorizationCode;
        console.log('Found token in result.response.authorizationCode');
      } else {
        console.error('Could not find identity token. Result structure:', {
          hasResult: !!result,
          hasResponse: !!result?.response,
          responseKeys: result?.response ? Object.keys(result.response) : [],
          resultKeys: result ? Object.keys(result) : []
        });
      }
      
      if (identityToken) {
        console.log('Identity token found, length:', identityToken.length);
        // Send the identity token to backend (this is the JWT token)
        const response = await axios.post(`${config.API_BASE_URL}/google-login`, {
          credential: identityToken, // Identity token is a JWT, same format as web
        }, { 
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        console.log('Native Google SSO: Response received:', response.data);
        
        if (response.data && response.data.user) {
          const user = response.data.user;
          console.log('Native Google SSO: User data:', user);
          // Store user info in persistent storage (survives app restarts on mobile)
          await persistentStorage.set('currentUser', JSON.stringify(user));
          await persistentStorage.set('currentUserId', resolveUserId(user, 'login'));
          await persistentStorage.set('currentUserEmail', user.email);
          // Store user name for display
          if (user.name) {
            await persistentStorage.set('currentUserName', user.name);
          }
          // Clear phone if it exists (for email-based users)
          await persistentStorage.remove('currentUserPhone');
          // Set role for routing
          if (setRole && user.role) {
            setRole(user.role);
          }
          // Mark session start (TTL)
          await markSessionStart();
          // Navigate directly to user dashboard
          navigate('/user-dashboard');
        } else {
          console.error('Native Google SSO: No user data in response');
          setError('Login failed. No user data received.');
        }
      } else {
        // Log detailed error information
        console.error('Google Sign-In failed - no identity token found');
        console.error('Full result object:', result);
        throw new Error(`Google Sign-In failed to return authentication data. Result: ${JSON.stringify(result)}`);
      }
      
      // Log user info if available
      if (result?.response) {
        console.log('User info from Google Sign-In:', {
          email: result.response.email,
          givenName: result.response.givenName,
          familyName: result.response.familyName,
          user: result.response.user
        });
      }
    } catch (error) {
      console.error('Native Google SSO Error:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        platform: Capacitor.getPlatform(),
        pluginAvailable: !!GoogleSignIn,
        methodsAvailable: GoogleSignIn ? Object.keys(GoogleSignIn) : []
      });
      
      let errorMessage = error.message || 'Google Sign-In failed. Please try again.';
      
      // Provide helpful error messages for common issues
      if (error.message && error.message.includes('not implemented')) {
        errorMessage = 'Google Sign-In plugin is not properly synced. Please run: npx cap sync android && npx cap sync ios';
      } else if (error.message && error.message.includes('not available')) {
        errorMessage = 'Google Sign-In plugin is not available. Please ensure capacitor-google-sign-in is installed and synced.';
      } else if (error.code === '10') {
        errorMessage = 'Google Sign-In was cancelled.';
      } else if (error.code === '12500') {
        errorMessage = 'Google Sign-In is already in progress. Please wait.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Google Sign-In for web platform
  const handleWebGoogleSSO = async (credentialResponse) => {
    setIsLoading(true);
    setError(''); // Clear any previous errors
    
    try {
      console.log('Web Google SSO: Sending credential to backend...');
      const response = await axios.post(`${config.API_BASE_URL}/google-login`, {
        credential: credentialResponse.credential,
      }, { 
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Web Google SSO: Response received:', response.data);
      
      if (response.data && response.data.user) {
        const user = response.data.user;
        console.log('Web Google SSO: User data:', user);
        // Store user info in persistent storage (survives app restarts on mobile)
        await persistentStorage.set('currentUser', JSON.stringify(user));
        await persistentStorage.set('currentUserId', resolveUserId(user, 'login'));
        await persistentStorage.set('currentUserEmail', user.email);
        // Store user name for display
        if (user.name) {
          await persistentStorage.set('currentUserName', user.name);
        }
        // Clear phone if it exists (for email-based users)
        await persistentStorage.remove('currentUserPhone');
        // Set role for routing
        if (setRole && user.role) {
          setRole(user.role);
        }
        // Mark session start (TTL)
        await markSessionStart();
        // Navigate directly to user dashboard (customers always go to user-dashboard)
        navigate('/user-dashboard');
      } else {
        console.error('Web Google SSO: No user data in response');
        setError('Login failed. No user data received.');
      }
    } catch (error) {
      console.error('Web Google SSO Error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      const errorMessage = error.response?.data?.error || error.message || 'Google SSO failed. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Unified handler that routes to web or native based on platform
  const handleGoogleSSO = async (credentialResponse) => {
    // Check if running on native platform (Android/iOS)
    if (Capacitor.isNativePlatform()) {
      await handleNativeGoogleSignIn();
    } else {
      // Web platform - use the credential response from GoogleLogin component
      await handleWebGoogleSSO(credentialResponse);
    }
  };

  const handleBack = () => {
    if (loginMethod === 'otp' || loginMethod === 'password') {
      // Go back to phone number input
      setLoginMethod('check');
      setOtp('');
      setPassword('');
      setUserInfo(null);
      setError('');
    } else {
      // Go back to home
      navigate('/');
    }
  };


  const resetForm = () => {
    setPhone('');
    setOtp('');
    setPassword('');
    setLoginMethod('check');
    setError('');
    setUserInfo(null);
  };

  useEffect(() => {
    setError('');
  }, [phone, otp, password]);

  const getButtonText = () => {
    if (isLoading) return '';
    switch (loginMethod) {
      case 'check':
        return 'Continue';
      case 'otp':
        return 'Verify OTP';
      case 'password':
        return 'Login with Password';
      default:
        return 'Continue';
    }
  };

  const handleButtonClick = () => {
    switch (loginMethod) {
      case 'check':
        return handleCheckUser();
      case 'otp':
        return handlePhoneLogin();
      case 'password':
        return handlePasswordLogin();
      default:
        return handleCheckUser();
    }
  };

  const isValidPhone = (phoneNumber) => {
    return /^\d{10}$/.test(phoneNumber);
  };

  const isButtonDisabled = () => {
    switch (loginMethod) {
      case 'check':
        return !phone.trim() || !isValidPhone(phone) || isLoading;
      case 'otp':
        return !otp.trim() || isLoading;
      case 'password':
        return !password.trim() || isLoading;
      default:
        return !phone.trim() || !isValidPhone(phone) || isLoading;
    }
  };

  return (
    <div className={classes.rootContainer}>
      <div className={classes.loginCard}>
        {/* Header Section */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          paddingTop: 82,
          paddingBottom: 20
        }}>
          {/* Logo */}
          <img 
            src={ParkingImage} 
            alt="NimblePark Logo" 
            style={{ width: 126, height: 114 }}
          />
          
          {/* Tagline */}
          <div style={{
            color: '#C084FC',
            fontSize: 18,
            fontFamily: 'Sansation',
            fontWeight: '700',
            lineHeight: '19.10px',
            marginTop: 20,
            textAlign: 'center'
          }}>
            Where Every Minute Counts
          </div>
        </div>

        {/* Form Container */}
        <div style={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: '0 20px',
          boxSizing: 'border-box',
          justifyContent: 'flex-start',
          gap: 20,
          marginTop:'1rem'
        }}>
          {/* User Welcome Message */}
          {userInfo && loginMethod === 'otp' && (
            <Typography style={{
              textAlign: 'center',
              color: '#C084FC',
              fontSize: 16,
              fontFamily: 'Nunito',
              fontWeight: '600',
              marginBottom: 10
            }}>
              Hi {userInfo.name}, please verify your phone number
            </Typography>
          )}

          {/* User Welcome Message for Password Login */}
          {userInfo && loginMethod === 'password' && (
            <Typography style={{
              textAlign: 'center',
              color: '#C084FC',
              fontSize: 16,
              fontFamily: 'Nunito',
              fontWeight: '600',
              marginBottom: 10
            }}>
              Hi {userInfo.name}, please enter your password
            </Typography>
          )}

          {/* Car Owner Login Header */}
          {(loginMethod === 'check' || loginMethod === 'password') && (
            <Typography style={{
              color: '#C084FC',
              fontSize: 14,
              fontFamily: 'Poppins',
              fontWeight: '600',
              textAlign: 'left',
              marginBottom: -5
            }}>
              Car Owner Login
            </Typography>
          )}

          {/* Phone Input */}
          {loginMethod === 'check' && (
            <TextField
              className={classes.phoneInput}
              fullWidth
              placeholder="Enter your phone number"
              type="tel"
              variant="outlined"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              inputProps={{ inputMode: 'tel', maxLength: 10 }}
              disabled={isLoading}
            />
          )}

          {/* OTP Input - Only show when OTP is sent */}
          {loginMethod === 'otp' && (
            <TextField
              className={classes.otpInput}
              fullWidth
              placeholder="Enter OTP"
              type="text"
              variant="outlined"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              disabled={isLoading}
            />
          )}

          {/* Password Input - Only show when password login is required */}
          {loginMethod === 'password' && (
            <TextField
              className={classes.otpInput}
              fullWidth
              placeholder="Enter password"
              type="password"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
          )}

          {/* Sign In Button */}
          <button
            className={classes.signInButton}
            onClick={handleButtonClick}
            disabled={isButtonDisabled()}
          >
            {isLoading ? (
              <CircularProgress size={20} className={classes.loadingSpinner} />
            ) : (
              getButtonText()
            )}
          </button>

          {/* Sign Up Text */}
          <div style={{
            textAlign: 'center',
            fontSize: 14,
            fontFamily: 'Poppins',
            fontWeight: '500',
            marginTop: 10
          }}>
            <span style={{ color: '#B6B6B6' }}>Don't have an account? </span>
            <Link to="/signup" style={{ color: '#C084FC', textDecoration: 'none' }}>
              Sign up
            </Link>
          </div>

          {/* Terms and Privacy Policy Links */}
          <div style={{ 
            textAlign: 'center',
            fontSize: 12,
            color: '#B6B6B6',
            fontFamily: 'Poppins',
            lineHeight: '16px',
            marginTop: 10,
            padding: '0 10px',
            display: 'flex',
            justifyContent: 'center',
            gap: 20
          }}>
            <span 
              style={{ 
                color: '#C084FC', 
                cursor: 'pointer', 
                textDecoration: 'underline',
                fontWeight: '500'
              }}
              onClick={() => setTermsOpen(true)}
            >
              Terms and Conditions
            </span>
            <span 
              style={{ 
                color: '#C084FC', 
                cursor: 'pointer', 
                textDecoration: 'underline',
                fontWeight: '500'
              }}
              onClick={() => setPrivacyOpen(true)}
            >
              Privacy Policy
            </span>
          </div>

          {/* Google Sign-In Button */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
            marginTop: 20,
            padding: '0 20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              gap: 10,
              marginBottom: 10
            }}>
              <div style={{ flex: 1, height: 1, backgroundColor: '#CCCCCC' }}></div>
              <span style={{ color: '#B6B6B6', fontSize: 14, fontFamily: 'Poppins' }}>or</span>
              <div style={{ flex: 1, height: 1, backgroundColor: '#CCCCCC' }}></div>
        </div>

            {/* Show native button for Android/iOS, side-by-side icon buttons for browser */}
            {Capacitor.isNativePlatform() ? (
              <Box sx={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Google Sign-In temporarily disabled — will re-enable later.
                <Button
                  onClick={handleNativeGoogleSignIn}
                  disabled={isLoading}
                  variant="outlined"
                  startIcon={<GoogleIcon />}
                  sx={{
                    width: '100%',
                    padding: '10px 20px',
                    borderColor: '#dadce0',
                    color: '#3c4043',
                    textTransform: 'none',
                    fontSize: '14px',
                    fontFamily: 'Poppins',
                    '&:hover': {
                      borderColor: '#dadce0',
                      backgroundColor: '#f8f9fa'
                    }
                  }}
                >
                  {isLoading ? 'Signing in...' : 'Sign in with Google'}
                </Button>
                */}
                {Capacitor.getPlatform() === 'ios' && (
                  <Button
                    onClick={handleNativeAppleSSO}
                    disabled={isLoading}
                    variant="contained"
                    startIcon={<AppleIcon />}
                    sx={{
                      width: '100%',
                      padding: '10px 20px',
                      backgroundColor: '#000000',
                      color: '#FFFFFF',
                      textTransform: 'none',
                      fontSize: '14px',
                      fontFamily: 'Poppins',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: '#1a1a1a',
                        boxShadow: 'none'
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#000000',
                        color: '#FFFFFF',
                        opacity: 0.6
                      }
                    }}
                  >
                    {isLoading ? 'Signing in...' : 'Sign in with Apple'}
                  </Button>
                )}
              </Box>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 16,
                flexWrap: 'wrap',
                width: '100%',
              }}>
                {/* Google Sign-In temporarily disabled — will re-enable later.
                <GoogleLogin
                  onSuccess={handleGoogleSSO}
                  onError={(error) => {
                    console.error('Google Login Error:', error);
                    setError('Google login failed. Please try again.');
                  }}
                  useOneTap={false}
                  theme="outline"
                  size="large"
                  text="signin_with"
                  shape="rectangular"
                  logo_alignment="left"
                />
                */}
                <IconButton
                  onClick={handleWebFacebookSSO}
                  disabled={isLoading}
                  aria-label="Continue with Facebook"
                  title="Continue with Facebook"
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: '#1877F2',
                    color: '#FFFFFF',
                    borderRadius: '4px',
                    '&:hover': { backgroundColor: '#166FE5' },
                    '&.Mui-disabled': { backgroundColor: '#1877F2', opacity: 0.6, color: '#FFFFFF' },
                  }}
                >
                  <FacebookIcon />
                </IconButton>
                <IconButton
                  onClick={handleWebAppleSSO}
                  disabled={isLoading}
                  aria-label="Continue with Apple"
                  title="Continue with Apple"
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: '#000000',
                    color: '#FFFFFF',
                    borderRadius: '4px',
                    '&:hover': { backgroundColor: '#1a1a1a' },
                    '&.Mui-disabled': { backgroundColor: '#000000', opacity: 0.6, color: '#FFFFFF' },
                  }}
                >
                  <AppleIcon />
                </IconButton>
              </div>
            )}
        </div>
        </div>
      

        {/* Footer Section */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          padding: '0px 20px 20px 20px',
          gap: 10,
          marginTop: 'auto',
          marginBottom: 10
        }}>
          {/* Associates Login Link */}
          <Link 
            to="/associates-login" 
            style={{ 
              color: '#C084FC', 
              textDecoration: 'none',
              fontSize: 14,
              fontFamily: 'Poppins',
              fontWeight: '500'
            }}
          >
            Associates Login
          </Link>

          {/* Error Message */}
          {error && (
            <Typography className={classes.errorText}>
              {error}
            </Typography>
          )}
        </div>

        {/* Terms and Conditions Dialog */}
        <Dialog 
          open={termsOpen} 
          onClose={() => setTermsOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: 12,
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle style={{ 
            fontFamily: 'Poppins', 
            fontWeight: '600',
            color: '#C084FC',
            textAlign: 'center'
          }}>
            Terms and Conditions
          </DialogTitle>
          <DialogContent style={{ padding: '10px' }}>
            <TermsOfService hideHeading={true} />
          </DialogContent>
          <DialogActions style={{ padding: '20px', justifyContent: 'center' }}>
            <Button 
              onClick={() => setTermsOpen(false)}
              style={{ 
                backgroundColor: '#C084FC', 
                color: 'white',
                fontFamily: 'Poppins',
                fontWeight: '500',
                padding: '10px 30px',
                borderRadius: 8
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Privacy Policy Dialog */}
        <Dialog 
          open={privacyOpen} 
          onClose={() => setPrivacyOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: 12,
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle style={{ 
            fontFamily: 'Poppins', 
            fontWeight: '600',
            color: '#C084FC',
            textAlign: 'center'
          }}>
            Privacy Policy
          </DialogTitle>
          <DialogContent style={{ padding: '10px' }}>
            <PrivacyPolicy hideHeading={true} />
          </DialogContent>
          <DialogActions style={{ padding: '20px', justifyContent: 'center' }}>
            <Button 
              onClick={() => setPrivacyOpen(false)}
              style={{ 
                backgroundColor: '#C084FC', 
                color: 'white',
                fontFamily: 'Poppins',
                fontWeight: '500',
                padding: '10px 30px',
                borderRadius: 8
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default Login;
